import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Film, Loader2, Coins, AlertTriangle, Save, Users } from "lucide-react";
import { toast } from "sonner";
import Thinking from "@/components/Thinking";
import { errorText } from "@/components/coaching/GuidedDay";
import { FightRounds } from "@/components/FightRounds";
import { PoseCheck } from "@/components/PoseCheck";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FilmAsk, FilmReports } from "@/components/FilmAsk";

const PRESETS = [
  "Read the scoreboard and give me every player's stats for this match.",
  "Count each player's involvements · shots, passes, tackles · and flag the three busiest.",
  "Who bowled/served what, and how did each player perform in the last 20 minutes?",
];

const STAT_LABELS = { lane: "Lane", placing: "Place", result_time_s: "Time (s)", result_reaction_s: "Reaction (s)", avg_speed_mps: "Speed (m/s)",
  split_15m_s: "15 m (s)", speed_15m_mps: "Speed to 15 m (m/s)", split_25m_s: "25 m (s)", split_50m_s: "50 m (s)", splits: "Splits", tracking_stroke_count: "Strokes",
  stroke_rate_spm: "Stroke rate (/min)", distance_per_stroke_m: "Dist/stroke (m)", breaths: "Breaths", breaths_per_length: "Breaths/length",
  breakout_distance_m: "Breakout (m)", breakout_time_s: "Breakout (s)", kicks_underwater: "UW kicks", result_distance_m: "Distance (m)",
  result_turn_count: "Turns", stroke_type: "Stroke" };

export default function SquadFilm({ squad = "", academyId = "", athleteId = "", initialAnalysisId = "", resultOnly = false }) {
  const [prompt, setPrompt] = useState(""), [batch, setBatch] = useState([]), [repKey, setRepKey] = useState(0);
  const [fixtures, setFixtures] = useState([]);
  const [fixtureId, setFixtureId] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [rows, setRows] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const loadHistory = useCallback(() => {
    api.get(`/squad/media/analyses?${new URLSearchParams({squad,academy_id:academyId})}`)
      .then((r) => setHistory(r.data.analyses || [])).catch(() => setHistory([]));
  }, [squad,academyId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => {
    api.get(`/fixtures?${new URLSearchParams({squad,academy_id:academyId})}`)
      .then((r) => setFixtures(r.data.fixtures || [])).catch(() => {});
    api.get("/athletes").then((r) => {
      const base = athleteId ? r.data.find((a) => a.athlete_id === athleteId) : null;
      const acad = base ? base.academy_id : academyId;
      setAthletes(r.data.filter((a) => base ? a.academy_id === base.academy_id || a.athlete_id === athleteId
        : (!squad || (a.sub_team || a.squad || a.sport) === squad || (acad && a.academy_id === acad)) && (!acad || (a.academy_id || '__unaffiliated__') === acad)));
    }).catch(() => {});
  }, [squad,academyId,athleteId]);

  const [job, setJob] = useState(null);
  const [drag, setDrag] = useState(false);
  const [corner, setCorner] = useState(""), [sport, setSport] = useState(""), [sports, setSports] = useState([]), [fails, setFails] = useState(0);
  useEffect(() => {
    let tries = 0, live = true;
    const get = () => api.get("/venues/coverage").then((r) => live && setSports(Object.keys(r.data.summary || {}).sort())).catch(() => { if (live && tries++ < 4) setTimeout(get, 3000); });
    get();
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (resultOnly || initialAnalysisId) return;
    api.get("/jobs").then(async (r) => {
      const j = r.data.jobs.find((x) => x.kind === "film" && ["running", "paused"].includes(x.status) && x.meta?.film_job_id && (x.meta.athlete_id || "") === athleteId && (athleteId || x.meta.squad === squad));
      if (!j) return;
      const { data } = await api.get(`/squad/media/jobs/${j.meta.film_job_id}`);
      setJob(data); setBusy(data.status === "running"); setSport(data.sport_override || ""); setCorner(data.corner || "");
    }).catch(() => {});
  }, [squad, athleteId, resultOnly, initialAnalysisId]);
  const combat = /box|mma|martial|kick|muay|taekwondo|wrestl|judo|fenc|gatka/i.test(sport || athletes[0]?.sport || "");
  const stale = job?.status === "running" && job.updated_at && Date.now() - new Date(job.updated_at).getTime() > 6 * 60 * 1000;

  const openAnalysis = useCallback(async (id) => {
    const { data } = await api.get(`/squad/media/analyses/${id}`);
    setRes(data); setFixtureId(data.fixture_id || fixtureId);
    setRows((data.result?.players || []).map((p, i) => { const aid = p.athlete_id || data.lane_map?.[p.name] || ""; return { ...p, athlete_id: aid, _i: i, include: !!aid && (data.result?.confidence ?? 0) >= 20 }; }));
  }, [fixtureId]);
  useEffect(() => { if (initialAnalysisId) openAnalysis(initialAnalysisId).catch(() => toast.error("That analysis could not be opened")); }, [initialAnalysisId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!job || !["running"].includes(job.status)) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/squad/media/jobs/${job.job_id}`);
        setJob(data); setFails(0);
        if (data.status === "done") { setBusy(false); loadHistory(); if (data.analysis_id) openAnalysis(data.analysis_id); (data.failed_parts?.length ? toast.warning : toast.success)(data.stage); }
        if (data.status === "error") { setBusy(false); toast.error(data.stage); }
        if (data.status === "paused") setBusy(false);
      } catch (e) {
        setFails((n) => n + 1);
        if (e?.response?.status === 404) { setBusy(false); setJob({ ...job, status: "error", stage: "This analysis no longer exists on the server. Upload the film again." }); }
      }
    }, 3000);
    return () => clearInterval(t);
  }, [job, loadHistory, openAnalysis]);

  useEffect(() => {
    if (!batch.some((b) => ["running", "queued"].includes(b.status))) return undefined;
    const t = setInterval(async () => {
      const next = await Promise.all(batch.map((b) => (["running", "queued"].includes(b.status) ? api.get(`/squad/media/jobs/${b.job_id}`).then((r) => ({ ...b, ...r.data })).catch(() => b) : b)));
      setBatch(next);
    }, 5000);
    return () => clearInterval(t);
  }, [batch]);

  const runAll = async (list) => {
    const files = Array.from(list || []);
    if (files.length < 2) return run(files[0]);
    const batchId = crypto.randomUUID().replace(/-/g, "").slice(0, 16), started = [];
    setRes(null);
    for (const f of files) { const j = await run(f, batchId); if (j) started.push({ job_id: j.job_id, filename: f.name, status: "running" }); setBatch([...started]); }
    if (started.length < files.length) toast.error(`${files.length - started.length} file(s) did not upload · the others are running`);
    setBusy(false);
  };

  const run = async (file, batchId = "") => {
    if (!file) return;
    if (!squad && !fixtureId && !athleteId) {
      return toast.error("Pick the squad or the fixture first", { description: "That is how the stats know which team they belong to." });
    }
    setBusy(true); setRes(null); setRows([]);
    try {
      const { data: up } = await api.post("/squad/media/uploads");
      const size = up.chunk_bytes;
      const total = Math.max(1, Math.ceil(file.size / size));
      for (let i = 0; i < total; i += 1) {
        setJob({ status: "uploading", stage: `Uploading ${Math.round((i / total) * 100)}% of ${(file.size / 1048576).toFixed(0)} MB`, progress: 0 });
        for (let attempt = 0; ; attempt += 1) {
          try { await api.put(`/squad/media/uploads/${up.upload_id}?index=${i}`, file.slice(i * size, (i + 1) * size), { headers: { "Content-Type": "application/octet-stream" }, timeout: 110000 }); break; }
          catch (err) { if (attempt >= 3 || [404, 413, 422].includes(err?.response?.status)) throw err; await new Promise((ok) => setTimeout(ok, 2000 * (attempt + 1))); }
        }
      }
      const { data } = await api.post("/squad/media/jobs", { upload_id: up.upload_id, filename: file.name, content_type: file.type || "", squad, academy_id: academyId, fixture_id: fixtureId, athlete_id: athleteId, prompt, corner, sport, batch_id: batchId });
      if (batchId) setJob(null); else setJob({ job_id: data.job_id, status: "running", stage: "Queued", progress: 1 });
      return data;
    } catch (e) {
      setBusy(false); setJob(null);
      toast.error(errorText(e));
    }
  };

  const resume = async (retry = false) => {
    try { await api.post(`/squad/media/jobs/${job.job_id}/${retry ? "retry-failed" : "resume"}`); setBusy(true); setJob({ ...job, status: "running" }); }
    catch (e) { toast.error(errorText(e)); }
  };

  const [dest, setDest] = useState("training"), [saveDate, setSaveDate] = useState(() => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); }), [newFx, setNewFx] = useState("");
  const num = (s) => Object.fromEntries(Object.entries(s || {}).map(([k, v]) => [k, typeof v === "string" && v.trim() !== "" && !isNaN(Number(v)) ? Number(v) : v]));
  const save = async () => {
    const picked = rows.filter((r) => r.include && r.athlete_id).map((r) => ({ ...r, stats: num(r.stats) }));
    if (!picked.length) return toast.error("Match at least one lane or player to an athlete first");
    setSaving(true);
    try {
      if (dest === "training") {
        const { data } = await api.post(`/squad/media/analyses/${res.analysis_id}/save-training`, { date: saveDate, lane_map: Object.fromEntries(picked.map((p) => [p.name, p.athlete_id])), players: picked.map((p) => ({ athlete_id: p.athlete_id, stats: p.stats })) });
        toast.success(`Saved as training results for ${data.saved} athlete(s) · they now feed their metric curves`);
        loadHistory(); window.dispatchEvent(new Event('trifit-synced'));
        return;
      }
      let fid = fixtureId, rev = fixtures.find(f=>f.fixture_id===fixtureId)?.result_revision || 0;
      if (fid === "__new") {
        const first = athletes.find((a) => a.athlete_id === picked[0].athlete_id) || {};
        const { data: fx } = await api.post("/fixtures", { type: "competition", date: saveDate, competition: newFx || res.result?.title || "Race", opponent: "",
          squad: first.sub_team || "", academy_id: first.academy_id || "", sport: res.sport || first.sport || "", athlete_ids: picked.map((p) => p.athlete_id) });
        fid = fx.fixture.fixture_id; rev = 0; setFixtureId(fid);
        api.get(`/fixtures?${new URLSearchParams({squad,academy_id:academyId})}`).then(r=>setFixtures([...(r.data.fixtures||[]).filter(f=>f.fixture_id!==fid), fx.fixture]));
      }
      if (!fid) return toast.error("Choose a fixture or create a new one");
      const { data } = await api.post(`/squad/media/analyses/${res.analysis_id}/assign`, {
        fixture_id: fid,
        lane_map: Object.fromEntries(picked.map((p) => [p.name, p.athlete_id])),
        expected_revision: rev,
        players: picked.map((p) => ({ athlete_id: p.athlete_id, stats: p.stats, physics: p.physics,
                                      physics_obs: p.physics_obs, movement: p.movement })),
      });
      toast.success(`Reviewed statistics saved to the fixture for ${data.saved} athlete(s)`);
      api.get(`/fixtures?${new URLSearchParams({squad,academy_id:academyId})}`).then(r=>setFixtures(r.data.fixtures||[]));
      window.dispatchEvent(new Event('trifit-synced'));
      loadHistory();
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Could not save match stats");
    } finally { setSaving(false); }
  };

  const teamStats = Object.entries(res?.result?.team_stats || {});
  const statKeys = Array.from(new Set(rows.flatMap((r) => Object.keys(r.stats || {})))).filter((k) => !["result_starts", "result_finishes"].includes(k)).slice(0, 24);
  const anyPhysics = rows.some((r) => r.physics && !r.physics.insufficient_data);
  const physicsLabel = (m) => {
    if (!m) return "";
    if (m.jump_height_cm != null) return `${m.jump_height_cm} cm jump`;
    if (m.release?.release_speed_ms != null) return `${m.release.release_speed_ms} m/s release`;
    if (m.flight_time_s != null) return `${m.flight_time_s} s flight`;
    return "";
  };

  return (
    <div className={`${resultOnly ? "" : "p-4 sm:p-8"} space-y-6 min-w-0`} data-testid="squad-film">
      {!resultOnly && (
      <div className="border border-border p-4 sm:p-6">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Film className="w-4 h-4" />
          <h2 className="font-heading font-bold tracking-tight">Match film → squad stats</h2>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
            <Coins className="w-3 h-3" /> Charged by usage as each part finishes
          </span>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Upload the match <b className="text-foreground">once for the whole squad</b>. You can upload a full-game film,
          a scoreboard photo, a PDF scorecard or a stats sheet.
        </p>

        <textarea data-testid="film-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2}
          className="inp mb-3" placeholder="What should we look for? (optional) · e.g. how do they build attacks, who presses most, which combinations does our boxer throw" />
        <details className="mb-3 border border-border px-3 py-2" data-testid="film-options"><summary data-testid="film-options-toggle" className="text-xs cursor-pointer">Options · optional, we detect the sport and pick the stats ourselves</summary>
        <div className="grid sm:grid-cols-2 gap-3 my-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Fixture (optional)</span>
            <select data-testid="film-fixture" value={fixtureId} onChange={(e) => setFixtureId(e.target.value)} className="inp mt-1">
              <option value="">Not linked to a fixture</option>
              {fixtures.map((f) => (
                <option key={f.fixture_id} value={f.fixture_id}>
                  {`${fmtDate(f.date)} · ${f.type}${f.opponent ? ` vs ${f.opponent}` : ""}`}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Quick asks</span>
            <select data-testid="film-preset" onChange={(e) => setPrompt(e.target.value)} value={prompt} className="inp mt-1">
              <option value="">Pick a quick ask…</option>{PRESETS.map((p) => <option key={p} value={p}>{`${p.slice(0, 58)}…`}</option>)}
            </select>
          </label>
        </div>

        <label className="block mb-3 max-w-xs"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Sport on the film</span>
          <select data-testid="film-sport" className="inp mt-1" value={sport} onChange={(e) => setSport(e.target.value)}>
            <option value="">{`Auto-detect${athletes[0]?.sport ? ` (squad plays ${athletes[0].sport})` : ""}`}</option>
            {sports.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
          </select></label>
        {combat && (
          <label className="block mb-3 max-w-xs"><span className="text-[10px] uppercase tracking-widest text-muted-foreground">Our corner</span>
            <select data-testid="film-corner" className="inp mt-1" value={corner} onChange={(e) => setCorner(e.target.value)}>
              <option value="">Read both corners</option><option value="red">Red corner</option><option value="blue">Blue corner</option>
            </select></label>
        )}
        </details>
        <label data-testid="film-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) runAll(e.dataTransfer.files); }}
          className={`block border-2 border-dashed rounded-xl p-8 text-center transition-colors ${drag ? "border-foreground bg-surface" : "border-border hover:border-foreground cursor-pointer"}`}>
          <input data-testid="film-input" type="file" multiple className="hidden" disabled={busy}
            accept="video/*,image/*,application/pdf,.csv,.xlsx,.xls"
            onChange={(e) => { runAll(e.target.files); e.target.value = ""; }} />
          <span className="text-sm text-muted-foreground">{busy ? "Working on the current file…" : "Drop one match, or several matches of a team to read their playstyle. Click to choose. Nothing else to set."}</span>
        </label>
        {job && (
          <div className="mt-3 border border-border p-3" data-testid="film-job">
            {["running", "uploading"].includes(job.status) && <Thinking label={job.status === "uploading" ? "Uploading" : "Analysing match evidence"} />}
            <p className="text-sm" data-testid="film-job-stage">{job.stage}</p>
            {stale && <p role="alert" className="text-xs text-traffic-amber" data-testid="film-job-stale">No progress for over 6 minutes. The server may have restarted; it resumes automatically, or upload again if this stays.</p>}
            {fails >= 4 && <p role="alert" className="text-xs text-traffic-amber" data-testid="film-job-offline">Lost contact with the server. Still retrying; your analysis keeps running there.</p>}
            {job.total_parts > 0 && <p className="text-xs text-muted-foreground tabular-nums" data-testid="film-job-meter">{`${job.done_parts || 0} of ${job.total_parts} parts read`}</p>}
            {job.failed_parts?.length > 0 && <p role="alert" className="text-xs text-traffic-amber mt-1" data-testid="film-job-gaps">{`Parts ${job.failed_parts.join(", ")} of ${job.total_parts} could not be read · not charged · the stats leave those minutes out`}</p>}
            {job.status === "done" && job.can_retry && <button data-testid="film-job-retry" onClick={() => resume(true)} className="text-sm px-4 py-2 mt-2 border border-foreground">Retry failed parts</button>}
            {job.status === "paused" && <div className="flex gap-2 mt-2"><button data-testid="film-job-resume" onClick={() => resume()} className="text-sm px-4 py-2 bg-foreground text-background">Resume</button><a data-testid="film-job-topup" href="/settings/billing" className="text-sm px-4 py-2 border border-border">Top up credits</a></div>}
          </div>
        )}
      </div>
      )}

      {batch.length > 0 && (!job || batch.length > 1) && (
        <section className="border border-border p-4 space-y-2" data-testid="film-batch">
          <h3 className="font-heading font-bold">{`${batch.length} films · ${batch.filter((b) => b.status === "done").length} done`}</h3>
          {batch.map((b) => <p key={b.job_id} className="text-xs" data-testid={`film-batch-${b.job_id}`}><b>{b.filename}</b>{` · ${b.status} · ${b.stage || "Queued"}`}</p>)}
          {batch.every((b) => b.status !== "running") && batch.some((b) => b.analysis_id) && <FilmAsk analysisIds={batch.filter((b) => b.analysis_id).map((b) => b.analysis_id)} defaultQuestion="Describe this team's playstyle across these matches: patterns, strengths, weaknesses and what we should train against." onSaved={() => setRepKey((k) => k + 1)} />}
        </section>
      )}
      {res && !resultOnly && <FilmAsk analysisIds={[res.analysis_id]} defaultQuestion="What stands out in this match, and what should we train next?" onSaved={() => setRepKey((k) => k + 1)} />}
      {!resultOnly && <FilmReports squad={squad} athleteId={athleteId} reloadKey={repKey} />}
      {res && (
        <ErrorBoundary key={res.analysis_id} title="This analysis has data the screen could not show. It is saved; reopen it from the list below." testid="film-result-error">
        <div className="border border-border" data-testid="film-result">
          <div className="px-4 sm:px-6 py-3 border-b border-border flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-heading font-bold tracking-tight">{res.result?.title || "Match film"}</span>
            {res.result?.score && <span className="text-sm">{res.result.score}</span>}
            {res.result?.opponent && <span className="text-sm text-muted-foreground">vs {res.result.opponent}</span>}
            <span className={`ml-auto text-xs tabular-nums ${res.result?.confidence >= 70 ? "text-traffic-green" : res.result?.confidence >= 40 ? "text-traffic-amber" : "text-traffic-red"}`}>
              {res.result?.confidence}% confidence
            </span>
          </div>

          {teamStats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-border" data-testid="film-team-stats">
              {teamStats.slice(0, 8).map(([k, v]) => (
                <div key={k} className="p-3 sm:p-4 border-r border-b border-border last:border-r-0">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{k.replace(/_/g, " ")}</div>
                  <div className="font-heading text-2xl font-black tabular-nums">{String(v)}</div>
                </div>
              ))}
            </div>
          )}

          <div className="scroll-x">
            <table className="w-full text-sm min-w-max" data-testid="film-player-table">
              <thead>
                <tr className="bg-surface border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 text-left">Save</th>
                  <th className="px-3 py-2 text-left">Seen on film</th>
                  <th className="px-3 py-2 text-left min-w-[180px]">Athlete</th>
                  {anyPhysics && <th className="px-3 py-2 text-left whitespace-nowrap">Physics from film</th>}
                  {statKeys.map((k) => <th key={k} className="px-3 py-2 text-left whitespace-nowrap">{STAT_LABELS[k] || k.replace(/^(result|event|tracking)_/, "").replace(/_/g, " ")}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p._i} className="border-b border-border last:border-0" data-testid={`film-row-${p._i}`}>
                    <td className="px-3 py-2">
                      <input type="checkbox" data-testid={`film-pick-${p._i}`} checked={p.include} disabled={!p.athlete_id}
                        onChange={(e) => setRows((rs) => rs.map((r) => r._i === p._i ? { ...r, include: e.target.checked } : r))} />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {p.jersey ? <span className="text-muted-foreground tabular-nums mr-1">#{p.jersey}</span> : null}
                      {p.name || "-"}
                      {p.side && <span className="block text-[10px] text-muted-foreground">{p.side}{!p.athlete_id ? " · not in your roster" : ""}</span>}
                    </td>
                    <td className="px-3 py-2">
                      <select data-testid={`film-athlete-${p._i}`} value={p.athlete_id || ""} className="inp text-xs py-1"
                        onChange={(e) => setRows((rs) => rs.map((r) => r._i === p._i
                          ? { ...r, athlete_id: e.target.value, include: !!e.target.value } : r))}>
                        <option value="">Not matched · skip</option>
                        {athletes.map((a) => <option key={a.athlete_id} value={a.athlete_id}>{a.name}{a.sub_team || a.sport ? ` · ${a.sub_team || a.sport}` : ""}</option>)}
                      </select>
                    </td>
                    {anyPhysics && (
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums" data-testid={`film-physics-${p._i}`}>
                        {physicsLabel(p.physics) || <span className="text-muted-foreground">-</span>}
                      </td>
                    )}
                    {statKeys.map((k) => (
                      <td key={k} className="px-3 py-2">
                        <input data-testid={`film-stat-${p._i}-${k}`} value={p.stats?.[k] ?? ""} className="inp text-xs py-1 w-20"
                          onChange={(e) => setRows((rs) => rs.map((r) => r._i === p._i
                            ? { ...r, stats: { ...r.stats, [k]: e.target.value } } : r))} />
                      </td>
                    ))}
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={3 + (anyPhysics ? 1 : 0) + statKeys.length} className="px-3 py-6 text-sm text-muted-foreground" data-testid="film-no-players">
                    No individual players could be read from this file. The team totals above are still usable.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          {(res.result?.rounds || []).length > 0 && <FightRounds rounds={res.result.rounds} corner={res.corner || ""} />}
          {res.result?.pose && !/swim/i.test(res.sport || "") && <PoseCheck pose={res.result.pose} rounds={res.result.rounds || []} sport={res.sport || ""} />}
          {(res.result?.caveats || []).length > 0 && (
            <ul className="px-4 sm:px-6 py-3 border-t border-border text-[11px] text-muted-foreground space-y-1" data-testid="film-caveats">
              {res.result.caveats.map((c, i) => <li key={i} className="flex gap-1.5"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />{c}</li>)}
            </ul>
          )}
          {res.result?.summary && <p className="px-4 sm:px-6 py-3 border-t border-border text-sm text-muted-foreground">{res.result.summary}</p>}

          <div className="px-4 sm:px-6 py-3 border-t border-border flex flex-wrap gap-2 items-center">
            <span className="text-[11px] text-muted-foreground flex-1">{res.result?.disclaimer}</span>
            <select data-testid="film-save-dest" value={dest} onChange={(e) => setDest(e.target.value)} className="inp text-sm w-auto">
              <option value="training">Save as training results</option>
              <option value="fixture">Save to a fixture</option>
            </select>
            {dest === "fixture" && <select data-testid="film-save-fixture" value={fixtureId} onChange={(e) => setFixtureId(e.target.value)} className="inp text-sm w-auto">
              <option value="">Choose fixture…</option>
              {fixtures.map((f) => <option key={f.fixture_id} value={f.fixture_id}>{`${fmtDate(f.date)} · ${f.competition || f.type}${f.opponent ? ` vs ${f.opponent}` : ""}`}</option>)}
              <option value="__new">+ New fixture</option>
            </select>}
            {dest === "fixture" && fixtureId === "__new" && <input data-testid="film-new-fixture-name" className="inp text-sm w-48" placeholder="Event name e.g. State meet 50m free" value={newFx} onChange={(e) => setNewFx(e.target.value)} />}
            {(dest === "training" || fixtureId === "__new") && <input data-testid="film-save-date" type="date" className="inp text-sm w-auto" value={saveDate} onChange={(e) => setSaveDate(e.target.value)} />}
            <button data-testid="film-save" onClick={save} disabled={saving || !rows.some((r) => r.include && r.athlete_id)}
              className="text-sm px-4 py-2 bg-foreground text-background hover:bg-neutral-800 transition-colors disabled:opacity-40 flex items-center gap-1.5">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {dest === "training" ? "Save to training" : "Save to fixture"} ({rows.filter((r) => r.include && r.athlete_id).length})
            </button>
          </div>
        </div>
        </ErrorBoundary>
      )}

      {!resultOnly && (
      <div className="border border-border" data-testid="film-history">
        <div className="px-4 sm:px-6 py-3 border-b border-border flex items-center gap-2">
          <Users className="w-4 h-4" />
          <span className="font-heading font-bold tracking-tight">Previous squad film</span>
        </div>
        {history.length === 0 ? (
          <p className="px-4 sm:px-6 py-5 text-sm text-muted-foreground" data-testid="film-history-empty">
            No squad film analysed yet. Your first upload will show up here with its stats.
          </p>
        ) : history.map((h) => (
          <div key={h.analysis_id} className="px-4 sm:px-6 py-3 border-b border-border last:border-0 flex flex-wrap gap-x-4 gap-y-1 text-sm"
            data-testid={`film-hist-${h.analysis_id}`}>
            <span className="font-medium">{h.filename}</span>
            <span className="text-muted-foreground">{h.squad} · {h.kind} · {(h.result?.players || []).length} player(s)</span>
            <span className="text-muted-foreground tabular-nums">{h.saved_training ? "saved to training" : ""}</span>
            {h.assigned && <span className="text-[10px] uppercase tracking-widest text-traffic-green">saved to records</span>}
            <span className="ml-auto text-xs text-muted-foreground tabular-nums">{fmtDateTime(h.at)}</span>
            <button data-testid={`film-hist-open-${h.analysis_id}`} onClick={() => openAnalysis(h.analysis_id)} className="text-xs underline">Open</button>
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
